/* El día en detalle: dónde se duerme (y cómo volver), el tiempo, las paradas en lista y, plegado,
   lo que toca ese día de cada documento: exámenes, ruta en coche, gestiones, compras y la noche. */
import { useRef, useState } from 'react';
import { estadoDe, marcaDe, useApuntes } from '../datos/apuntes.ts';
import { comprasDelDia, comprasEnCualquierSitio, esCabecera, tituloCompra } from '../datos/compras.ts';
import { cielo, tiempoDe, useTiempo } from '../datos/meteo.ts';
import { peligro } from '../datos/retraso.ts';
import { enlaces } from '../datos/reservas.ts';
import type { Dia, Lugar, Viaje } from '../datos/tipos.ts';
import type { BloqueV } from '../escena/bloques.ts';
import { bloquesDelDia, Casilla, Documento } from '../componentes/Documento.tsx';
import { Hoja } from '../componentes/Hoja.tsx';
import { euros, fechaCorta, hhmm, num2, titulo } from '../texto.ts';
import { ahoraMin } from '../tiempo.ts';
import { ListaExamenes } from './Examenes.tsx';
import type { PestanaGuia } from './Guia.tsx';

interface Props {
  v: Viaje;
  dia: Dia;
  bl: BloqueV[];
  esHoy: boolean;
  onIr: (b: BloqueV) => void;
  onHotel: (l: Lugar) => void;
  onGuia: (p: PestanaGuia) => void;
  onCerrada: () => void;
}

export function HojaDia({ v, dia, bl, esHoy, onIr, onHotel, onGuia, onCerrada }: Props) {
  useApuntes();
  const meteo = useTiempo(v);
  const now = ahoraMin();
  const hotel = dia.hotel ? v.lugares.find(l => l.id === dia.hotel) : undefined;
  const t = tiempoDe(meteo, dia);
  const noche = v.noches.find(x => x.dia === dia.n);
  const examenes = v.examenes.filter(e => e.dia === dia.n);
  const coche = v.coche.dias.find(c => c.dia === dia.n);
  const pel = esHoy ? peligro(bl, dia.n, marcaDe) : null;
  const compras = comprasDelDia(v.compras, dia).filter(c => !esCabecera(c) || !v.compras.some(x => x.lista === 'mias' && !esCabecera(x) && x.categoria === c.categoria))
    // Primero lo importante: así las 5 que se ven son las que cuentan (no se reordena al marcar)
    .map((c, i) => ({ c, i, k: { A: 0, B: 1, C: 2 }[c.prioridad ?? ''] ?? 3 }))
    .sort((a, b) => a.k - b.k || a.i - b.i).map(x => x.c);
  const [todasCompras, setTodasCompras] = useState(false);
  const VISIBLES = 5;
  const docsDelDia = (['preparativos', 'consideraciones'] as const).filter(k => v.documentos[k].secciones.some(s => bloquesDelDia(s, dia.n).length));
  // Lo opcional del día (Pokémon Café, karaokes, museos…): con la parada donde encaja, si la hay
  const enl = enlaces(v);
  const sitioDe = (id: string) => bl.find(b => enl.porBloque.get(b.id)?.some(r => r.id === id));
  const opcionales = v.reservas.filter(r => r.dia === dia.n && r.tipo === 'actividad' && /OPCIONAL/.test(r.estado ?? '')
    || r.dia === dia.n && r.tipo === 'actividad' && enl.sueltas.includes(r) && /FLEXIBLE/.test(r.estado ?? ''));
  const gestiones = docsDelDia.reduce((n, k) => n + v.documentos[k].secciones.reduce((m, s) => m + bloquesDelDia(s, dia.n).length, 0), 0);

  return (
    <Hoja id="hoja-dia" titulo="dia-titulo" onCerrada={onCerrada}>
      {cerrar => (
        <>
          <p className="hoja__kicker">JP{num2(dia.n)} · {fechaCorta(dia).toUpperCase()} · {dia.zona.toUpperCase()}</p>
          <h2 id="dia-titulo">{titulo(dia.titulo)}</h2>
          {dia.enfoque && <p className="texto">{dia.enfoque}</p>}
          <dl className="fichas">
            {hotel
              ? <button type="button" className="ficha ficha--ancha ficha--boton" onClick={() => onHotel(hotel)}><dt>Duermes en · cómo volver</dt><dd>{dia.dormir} <span aria-hidden="true">›</span></dd></button>
              : <div className="ficha ficha--ancha"><dt>Duermes en</dt><dd>{dia.dormir}</dd></div>}
            <div className="ficha"><dt>Tiempo</dt><dd>{t ? <><span lang="ja" className="kanji-tiempo">{cielo(t.codigo)[0]}</span> {cielo(t.codigo)[1]} · {Math.round(t.max)}° / {Math.round(t.min)}°{t.lluvia >= 20 ? ` · lluvia ${t.lluvia} %` : ''}</> : 'Aún sin previsión (llega a 16 días vista)'}</dd></div>
            <div className="ficha"><dt>Intensidad</dt><dd>{dia.intensidad}</dd></div>
            <div className="ficha"><dt>Horas fijas</dt><dd>{dia.horasFijas}</dd></div>
            <div className="ficha"><dt>Noche</dt><dd>{dia.nochePrevista}{noche ? ` · salir: ${noche.salir.toLowerCase()}` : ''}</dd></div>
          </dl>

          {pel && (
            <div className="aviso aviso--peligro" role="alert">
              <b>▲ Vas {pel.retraso} min tarde.</b> Llegarías a «{pel.ancla.que}» a las {hhmm(pel.llegas)} ({pel.tarde} min después de las {pel.ancla.t.iniTxt}).
              {pel.saltables.length > 0 && <> Si saltas {pel.saltables.map(b => `«${b.que}» (${b.pri})`).join(' y ')}, llegas a tiempo.</>}
            </div>
          )}

          {examenes.length > 0 && <><h3>Exámenes de hoy</h3><ListaExamenes v={v} examenes={examenes} /></>}

          <h3>Paradas del día · {bl.length}</h3>
          <ol className="lista">
            {bl.map(b => {
              const r = estadoDe(b.id), aqui = esHoy && b.t.ini <= now && now < b.t.fin;
              return (
                <li key={b.id} className={`${aqui ? 'is-ahora' : ''} ${r === 'saltado' ? 'is-saltado' : ''}`}>
                  <button type="button" onClick={() => { cerrar(); onIr(b); }}>
                    <time>{b.t.iniTxt}</time><span className="pri" data-pri={b.pri}>{b.pri}</span><span className="que">{b.que}<small className="que__dur">{b.t.iniTxt}{b.t.fin > b.t.ini ? `–${b.t.finTxt} · ${b.t.fin - b.t.ini} min` : ''}{r === 'hecho' ? ' · realizado' : r === 'saltado' ? ' · no realizado' : ''}</small></span><span className="marca" lang="ja">{r === 'hecho' ? '済' : b.examen ? '試' : aqui ? '●' : ''}</span>
                  </button>
                </li>
              );
            })}
          </ol>

          {opcionales.length > 0 && (
            <details className="plegable">
              <summary>Si te apetece hoy · {opcionales.length}</summary>
              <div><ul className="opcionales">
                {opcionales.map(r => {
                  const b = sitioDe(r.id);
                  const cuerpo = <><time>{b?.t.iniTxt ?? (r.inicio && r.inicio !== '—' ? r.inicio : '—')}</time><span className="que">{titulo(r.que)}{b && <small> · en {b.que}</small>}</span>{r.importe ? <span className="precio">{euros(r.importe)}</span> : null}</>;
                  return <li key={r.id}>{b ? <button type="button" onClick={() => { cerrar(); onIr(b); }}>{cuerpo}</button> : <div>{cuerpo}</div>}</li>;
                })}
              </ul></div>
            </details>
          )}

          {coche ? (
            <details className="plegable" open>
              <summary>Ruta en coche · {coche.paradas.length} paradas</summary>
              <div>
                <RutaParaCopiar paradas={coche.paradas} lugarDe={p => v.lugares.find(l => l.id === p.lugar)} esHoy={esHoy} />
                <h4 className="doc-sub">Paradas</h4>
                {coche.paradas.map((p, i) => <ParadaCoche key={i} p={p} lugar={v.lugares.find(l => l.id === p.lugar)} />)}
                {coche.carreteras.length > 0 && <><h4 className="doc-sub">Carreteras</h4>{coche.carreteras.map((c, i) => <p key={i}><b>{c.tramo}</b> · {c.por}</p>)}</>}
                {coche.rutas.length > 0 && <div className="enlaces">{coche.rutas.map(r => <a key={r.url} className="enlace" href={r.url} target="_blank" rel="noopener">{r.nombre} ↗</a>)}</div>}
                {v.coche.avisos.length > 0 && <><h4 className="doc-sub">Avisos</h4>{v.coche.avisos.map((a, i) => <p key={i}><b>{a.tema}</b> · {a.detalle}</p>)}</>}
              </div>
            </details>
          ) : dia.paradas.length > 0 && (
            <details className="plegable">
              <summary>Paradas por el camino · {dia.paradas.length}</summary>
              <div>{dia.paradas.map((p, i) => <p key={i}><b>{p.hora} · {p.nombre}</b><br />{p.nota} {p.url && <a href={p.url} target="_blank" rel="noopener">Maps ↗</a>}</p>)}</div>
            </details>
          )}

          {dia.goshuin?.length ? (
            <details className="plegable" open>
              <summary>Goshuin del día · {dia.goshuin.length} objetivos</summary>
              <div>
                <p className="texto" style={{ fontSize: '.85rem' }}>Objetivos, no conseguidos: solo se marcan cuando lo confirméis.</p>
                <ul className="goshuin">
                  {dia.goshuin.map(g => <li key={g.templo}><b>{g.templo}</b> <span className="goshuin__estado" data-estado={g.estado}>{g.estado.toLowerCase()}</span>{g.detalle && <small>{g.detalle}</small>}</li>)}
                </ul>
              </div>
            </details>
          ) : null}

          {dia.candidatos?.length ? (
            <details className="plegable">
              <summary>Templos candidatos · no caben · {dia.candidatos.length}</summary>
              <div>
                <p className="texto" style={{ fontSize: '.85rem' }}>Lugares famosos de Kioto que no entran en la ruta. No están visitados.</p>
                <ul className="candidatos">
                  {dia.candidatos.map(c => (
                    <li key={c.nombre}>
                      <p><span className="pri" data-pri={c.prioridad[0]}>{c.prioridad}</span> <b>{c.nombre}</b> · {c.motivo}</p>
                      <small>{c.porQueNo} <a href={`https://www.google.com/maps/search/?api=1&query=${c.lat},${c.lon}`} target="_blank" rel="noopener">Maps ↗</a></small>
                    </li>
                  ))}
                </ul>
              </div>
            </details>
          ) : null}

          {dia.planesAnteriores?.length ? (
            <details className="plegable plan-anterior">
              <summary>Planes anteriores · {dia.planesAnteriores.length}</summary>
              <div>
                <p className="texto" style={{ fontSize: '.85rem' }}>Versiones que hubo antes del cambio, de la más antigua a la más reciente. Solo como referencia: el plan activo es el de arriba.</p>
                {dia.planesAnteriores.map((pa, j) => (
                  <div key={j}>
                    <h4 className="doc-sub">{pa.origen}{pa.titulo ? ` · ${pa.titulo}` : ''}</h4>
                    <ul className="plan-anterior__lista">
                      {pa.bloques.map((b, i) => <li key={i}><time>{b.hora}</time> <span className="pri" data-pri={b.pri}>{b.pri}</span> {b.que}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </details>
          ) : null}

          {gestiones > 0 && (
            <details className="plegable">
              <summary>Gestiones de hoy · {gestiones}</summary>
              <div>{docsDelDia.map(k => <Documento key={k} doc={v.documentos[k]} dia={dia.n} />)}</div>
            </details>
          )}

          {compras.length > 0 && (
            <details className="plegable">
              <summary>Compras por aquí · {compras.length}</summary>
              <div>
                {(todasCompras ? compras : compras.slice(0, VISIBLES)).map(c => <Casilla key={c.id} id={c.id}><b>{tituloCompra(c)}</b><small className="casilla__meta">{c.donde}</small></Casilla>)}
                {!todasCompras && compras.length > VISIBLES && <p><button type="button" className="enlace-texto" onClick={() => setTodasCompras(true)}>Ver las {compras.length - VISIBLES} restantes</button></p>}
                <p>Y en cualquier Donki o súper, {comprasEnCualquierSitio(v.compras).length} más: <button type="button" className="enlace-texto" onClick={() => onGuia('compras')}>ver compras</button></p>
              </div>
            </details>
          )}

          {noche && (
            <details className="plegable">
              <summary>Noche · salir: {noche.salir.toLowerCase()}</summary>
              <div>
                <p><b>Cita:</b> {noche.cita.toLowerCase()}</p>
                <p>{noche.lectura}</p>
                <p><button type="button" className="enlace-texto" onClick={() => onGuia('noche')}>Toda la estrategia de la noche</button></p>
              </div>
            </details>
          )}

          {dia.mapas.length > 0 && (
            <div className="enlaces">{dia.mapas.map(m => <a key={m.url} className="enlace" href={m.url} target="_blank" rel="noopener">{m.nombre.replace(/^D\d+ · /, '')} ↗</a>)}</div>
          )}
          {(dia.planb || dia.control.length > 0) && (
            <details className="plegable">
              <summary>Si algo falla · plan B</summary>
              <div>{dia.planb && <p>{dia.planb}</p>}{dia.control.map((c, i) => <p key={i}>· {c}</p>)}</div>
            </details>
          )}
          <button type="button" className="guia-enlace" onClick={() => onGuia('antes')}><span lang="ja" aria-hidden="true">案内</span>La guía del viaje · compras, frases, hoteles, cambio…</button>
        </>
      )}
    </Hoja>
  );
}

type ParadaC = Viaje['coche']['dias'][number]['paradas'][number];
const coord = (l?: Lugar) => (l?.lat != null && l.lon != null && l.precision !== 'dudosa' ? `${l.lat.toFixed(5)},${l.lon.toFixed(5)}` : null);
// dir_action=navigate: Google Maps empieza la navegación guiada desde donde estás, sin pulsar «Iniciar»
export const navegarMaps = (c: string) => `https://www.google.com/maps/dir/?api=1&destination=${c}&travelmode=driving&dir_action=navigate`;
export const navegarWaze = (c: string) => `https://waze.com/ul?ll=${c}&navigate=yes`;

/** Copia al portapapeles (y si el navegador no deja, lo selecciona para copiarlo a mano). */
export async function copiar(texto: string, el?: HTMLElement | null): Promise<boolean> {
  try { await navigator.clipboard.writeText(texto); return true; }
  catch {
    if (el) { const r = document.createRange(); r.selectNodeContents(el); const sel = getSelection(); sel?.removeAllRanges(); sel?.addRange(r); }
    return false;
  }
}

export function ParadaCoche({ p, lugar }: { p: ParadaC; lugar?: Lugar }) {
  const c = coord(lugar);
  const ref = useRef<HTMLElement>(null);
  const [copiado, setCopiado] = useState(false);
  return (
    <div className="parada-coche">
      <p><b>{p.opcional ? `Opcional ${p.opcional}` : p.hora} · {p.nombre}</b>{p.modo && p.modo !== '—' ? <small> · {p.modo.toLowerCase()}</small> : null}</p>
      {p.nota && <p className="texto">{p.nota}</p>}
      {c && (
        <div className="gps">
          <code ref={ref} className="gps__coord">{c}</code>{lugar?.precision === 'zona' && <small> aprox.</small>}
          <div className="gps__botones">
            <button type="button" className="gps__boton" onClick={() => void copiar(c, ref.current).then(ok => { setCopiado(ok); setTimeout(() => setCopiado(false), 1800); })}>{copiado ? '✓ Copiado' : '📋 Copiar'}</button>
            <a className="gps__boton" href={navegarMaps(c)} target="_blank" rel="noopener">Maps ▶</a>
            <a className="gps__boton" href={navegarWaze(c)} target="_blank" rel="noopener">Waze ▶</a>
          </div>
        </div>
      )}
      {(p.sitio || p.ruta) && (
        <p className="parada-coche__botones">
          {p.sitio && <a className="enlace" href={p.sitio} target="_blank" rel="noopener">Sitio ↗</a>}
          {p.ruta && <a className="enlace" href={p.ruta} target="_blank" rel="noopener">Ruta ↗</a>}
        </p>
      )}
    </div>
  );
}

/** Toda la ruta del coche: por tramos para Google Maps (como mucho 10 puntos por enlace) y en texto para copiar. */
function RutaParaCopiar({ paradas, lugarDe, esHoy }: { paradas: ParadaC[]; lugarDe: (p: ParadaC) => Lugar | undefined; esHoy: boolean }) {
  const ref = useRef<HTMLPreElement>(null);
  const [copiado, setCopiado] = useState(false);
  const [elegida, setElegida] = useState<number | null>(null);
  // Solo las paradas fijas con sitio (las opcionales no entran en la ruta); sin repetir el mismo punto seguido
  const pts: { p: ParadaC; c: string }[] = [];
  for (const p of paradas) {
    const c = coord(lugarDe(p));
    if (!c || p.opcional || /^opc/i.test(p.hora ?? '') || pts.at(-1)?.c === c) continue;
    if (!pts.length && /tren/i.test(p.modo)) continue;           // hasta coger el coche se va en tren: la ruta empieza en el coche
    if (pts.length && /tren|taxi/i.test(p.modo)) break;          // y acaba al dejarlo (la vuelta de JDM es en taxi y tren)
    if (/pie/i.test(p.modo)) continue;                           // lo que se hace andando (teamLab) no es parada del coche
    pts.push({ p, c });
  }
  if (pts.length < 1) return null;
  // Siempre desde donde estás (sin origen, Google Maps sale de tu ubicación). En el móvil acepta 3 paradas
  // intermedias por enlace: tramos de 4 puntos que se abren en orden, cada uno desde donde estés entonces

  // La siguiente parada según la hora (lo de después de medianoche cuenta como tarde del mismo día)
  const minDe = (x: { p: ParadaC }, i: number) => { const m = x.p.ini ?? 0; return i > 0 && m < 300 ? m + 1440 : m; };
  const ahora = ahoraMin();
  const porHora = esHoy ? pts.findIndex((x, i) => minDe(x, i) >= ahora - 10) : 0;
  const idx = Math.min(pts.length - 1, Math.max(0, elegida ?? (porHora < 0 ? pts.length - 1 : porHora)));
  const siguiente = pts[idx] ?? null;
  const resto = pts.slice(idx);                 // lo que queda desde la parada elegida (por la hora o con ‹ ›)
  const tramos: typeof pts[] = [];
  for (let i = 0; i < resto.length; i += 4) tramos.push(resto.slice(i, i + 4));
  const enlace = (t: typeof pts) => `https://www.google.com/maps/dir/?api=1&destination=${t.at(-1)!.c}${t.length > 1 ? `&waypoints=${t.slice(0, -1).map(x => x.c).join('%7C')}` : ''}&travelmode=driving`;
  const texto = pts.map((x, i) => `${i + 1}. ${x.p.hora ?? ''} ${x.p.nombre} · ${x.c}`.replace(/\s+/g, ' ')).join('\n');
  return (
    <div className="ruta-copiar">
      <h4 className="doc-sub">Ruta para el GPS</h4>
      {siguiente && (
        <div className="siguiente-gps">
          <div className="siguiente-gps__cab">
            <button type="button" aria-label="Parada anterior" disabled={idx === 0} onClick={() => setElegida(idx - 1)}>‹</button>
            <p><small>Ir desde aquí a · {idx + 1} de {pts.length}{elegida == null && esHoy && porHora >= 0 ? ' · la siguiente' : ''}</small><b>{siguiente.p.hora} · {siguiente.p.nombre}</b></p>
            <button type="button" aria-label="Parada siguiente" disabled={idx === pts.length - 1} onClick={() => setElegida(idx + 1)}>›</button>
          </div>
          <div className="gps__botones">
            <a className="accion" href={navegarMaps(siguiente.c)} target="_blank" rel="noopener">▶ Ir con Google Maps</a>
            <a className="accion" href={navegarWaze(siguiente.c)} target="_blank" rel="noopener">▶ Ir con Waze</a>
          </div>
        </div>
      )}
      <p className="texto" style={{ fontSize: '.85rem' }}>Paso a paso: «▶ Ir» (o «Maps ▶» en cada parada) abre la navegación guiada desde donde estás. Todas las rutas salen de donde estés. La ruta completa abre de una vez todo lo que queda desde la parada de arriba (cámbiala con ‹ › para empezar en otra); si tu móvil corta alguna (en el navegador Google Maps solo acepta 3 intermedias), usa los tramos en orden. Waze va siempre parada a parada.</p>
      <div className="enlaces">
        {resto.length <= 10 && tramos.length > 1 && <a className="enlace enlace--completa" href={enlace(resto)} target="_blank" rel="noopener">Google Maps · desde aquí, todo lo que queda ({resto.length} paradas) ↗</a>}
        {tramos.map((t, i) => <a key={i} className="enlace" href={enlace(t)} target="_blank" rel="noopener">Google Maps · {tramos.length > 1 ? `tramo ${i + 1}: ` : ''}desde aquí → {t.map(x => x.p.nombre.split(' · ')[0]).join(' → ')} ↗</a>)}
      </div>
      <pre ref={ref} className="ruta-copiar__texto">{texto}</pre>
      <button type="button" className="accion" onClick={() => void copiar(texto, ref.current).then(ok => { setCopiado(ok); setTimeout(() => setCopiado(false), 1800); })}>{copiado ? '✓ Ruta copiada' : '📋 Copiar toda la ruta'}</button>
    </div>
  );
}
